import AddIcon from '@mui/icons-material/Add';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChairRoundedIcon from '@mui/icons-material/ChairRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import WaterDamageRoundedIcon from '@mui/icons-material/WaterDamageRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import WavesRoundedIcon from '@mui/icons-material/WavesRounded';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';

// ---- Icon helper (copied from your create form) ----
const getIconForKey = (key) => {
  switch (key) {
    case 'ColdWaterSupplyAirTemp':
      return (
        <AirRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(209, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'ColdWaterSupplyPipeTemp':
    case 'SupplyPipeFlow':
    case 'ReturnPipeFlow':
    case 'PipeFlow':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            transform: 'rotate(90deg)',
            color: 'hsla(209, 100%, 63%, 1)',
            mr: 1,
          }}
        />
      );
    case 'ComfortSensorTemp':
      return <ChairRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    case 'HeatingPipeSupplyTemp':
    case 'HeatingPipeReturnTemp':
    case 'HotWaterSupplyPipeTemp':
    case 'HeatingPipeTemp':
      return (
        <WavesRoundedIcon
          fontSize="small"
          sx={{
            color: 'hsla(8, 100%, 63%, 1)',
            transform: 'rotate(90deg)',
            mr: 1,
          }}
        />
      );
    case 'HotWaterSupplyAirTemp':
      return (
        <AirRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(8, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'PowerConsumption':
      return (
        <BoltRoundedIcon
          fontSize="small"
          sx={{ color: 'hsl(50, 100%, 50%)', mr: 1 }}
        />
      );
    case 'WaterConsumption':
      return (
        <WaterDropRoundedIcon
          fontSize="small"
          sx={{ color: 'hsla(209, 100%, 63%, 1)', mr: 1 }}
        />
      );
    case 'CO2Level':
      return <Co2RoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    case 'RelativeHumidity':
      return <WaterDamageRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
    default:
      return <QuestionMarkRoundedIcon fontSize="small" sx={{ mr: 1 }} />;
  }
};

// ---- Mapping helpers ----
// Map API subscription → formData the UI expects
const fromSubscriptionToFormData = (sub, locationFallback) => {
  if (!sub) return null;
  return {
    JobType: sub.JobType || '',
    DataInput: sub.DataInput || {},
    AlgorithmInput: sub.AlgorithmInput || {},
    UserInput: {
      Location: sub.UserInput?.Location ||
        locationFallback || { Address: '', Latitude: 0, Longitude: 0 },
      ControlType: sub.UserInput?.ControlType || 'pct',
      BuildingOccupiedCalendar: sub.UserInput?.BuildingOccupiedCalendar || [],
      BuildingEmptyCalendar: sub.UserInput?.BuildingEmptyCalendar || [],
      HeatingSystemType: sub.UserInput?.HeatingSystemType,
      RiskOfFrostDamage: Boolean(sub.UserInput?.RiskOfFrostDamage),
      GatewayId: sub.UserInput?.GatewayId,
      Channel: sub.UserInput?.Channel,
      DeviceNumber: sub.UserInput?.DeviceNumber,
      CircuitNumber: sub.UserInput?.CircuitNumber,
      Offset: sub.UserInput?.Offset,
      BuildingName: sub.UserInput?.BuildingName,
      HeatingCircuitName: sub.UserInput?.HeatingCircuitName,
    },
    AccountId: sub.AccountId || 0,
    DisplayName: sub.DisplayName || '',
  };
};

// Optionally strip any UI-only fields before submit
const toPayload = (formData) => ({ ...formData });

// Convenience getters for controlled Autocompletes
const getUnitById = (units, id) =>
  (units || []).find((u) => u.UnitId === id) || null;
const getSensorByIds = (units, unitId, unitTypeInputId) => {
  const unit = (units || []).find((u) => u.UnitId === unitId);
  return (
    unit?.UnitSensors?.find(
      (s) => s.UnitTypeInput.UnitTypeInputId === unitTypeInputId,
    ) || null
  );
};

const CHANNEL_OPTIONS = [
  { value: 'output0', label: 'Temperature probe override' },
  { value: 'output1', label: 'Relay 1' },
  { value: 'output2', label: 'Relay 2' },
  { value: 'output3', label: 'Relay 3' },
];

// ---- Standalone EDIT component ----
const JobSubscriptionEdit = ({
  open,
  onClose,
  submit,
  subscription, // ← existing subscription object to edit
  location,
  jobSpecifications,
  gateways,
  isLoading = false,
}) => {
  const initial = useMemo(
    () => fromSubscriptionToFormData(subscription, location),
    [subscription, location],
  );

  const [formData, setFormData] = useState(initial);
  const [showOptionalFields, setShowOptionalFields] = useState({});

  const { data: units } = useGetUnitsByAccountQuery(subscription?.AccountId, {
    skip: !subscription?.AccountId,
  });

  // Keep form in sync when opening or subscription changes
  useEffect(() => {
    if (!open) return;
    const next = fromSubscriptionToFormData(subscription, location);
    setFormData(next);

    // initialize optional toggles: required OR present in DataInput are shown
    const jobSpec = jobSpecifications?.find((j) => j.JobType === next?.JobType);
    const enabled = {};
    (jobSpec?.DataInputSpecifications || []).forEach((spec) => {
      if (spec.Required || (next?.DataInput && next.DataInput[spec.Key]))
        enabled[spec.Key] = true;
    });
    setShowOptionalFields(enabled);
  }, [open, subscription, location, jobSpecifications]);

  if (!formData) return null; // guard if no subscription yet

  const handleDataInputChange = (key, index, field, value) => {
    setFormData((prev) => {
      const dataInput = { ...(prev.DataInput ?? {}) };
      const list = Array.isArray(dataInput[key]) ? [...dataInput[key]] : [];
      const curr = { ...(list[index] ?? {}) };
      curr[field] = value;

      if (field === 'UnitTypeInputId') {
        const unit = getUnitById(units, curr.UnitId);
        const sensor = unit?.UnitSensors?.find(
          (s) => s.UnitTypeInput.UnitTypeInputId === curr.UnitTypeInputId,
        );
        if (sensor) {
          const validTypes = (
            sensor.UnitTypeInput.SensorRequirements || []
          ).map((r) => r.DataType);
          curr.DataType = validTypes.length === 1 ? validTypes[0] : '';
        }
      }

      if (curr.AggregateType == null) {
        const jobSpec = jobSpecifications?.find(
          (j) => j.JobType === prev.JobType,
        );
        const specDef = jobSpec?.DataInputSpecifications?.find(
          (s) => s.Key === key,
        );
        curr.AggregateType = specDef?.DefaultAggregateType || 'Minutes5';
      }

      // re-assemble
      list[index] = curr;
      dataInput[key] = list;

      return { ...prev, DataInput: dataInput };
    });
  };

  const handleUserInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      UserInput: { ...prev.UserInput, [key]: value },
    }));
  };

  const toggleOptionalField = (key) => {
    setShowOptionalFields((prev) => ({ ...prev, [key]: !prev[key] }));

    setFormData((prev) => {
      const next = { ...prev, DataInput: { ...prev.DataInput } };
      if (next.DataInput[key]) delete next.DataInput[key];
      return next;
    });
  };

  const validateForm = () => {
    const jobSpec = jobSpecifications?.find(
      (j) => j.JobType === formData.JobType,
    );
    let ok = true;

    (jobSpec?.DataInputSpecifications || []).forEach((spec) => {
      const inputs = formData.DataInput[spec.Key];
      if (spec.Required && (!inputs || inputs.length === 0)) {
        ok = false;
        alert(`Missing required data input for ${spec.DisplayName}`);
      }
      (inputs || []).forEach((input, idx) => {
        if (
          !input.UnitId ||
          !input.UnitTypeInputId ||
          !input.DataType ||
          !input.AggregateType
        ) {
          ok = false;
          alert(
            `Missing required fields for ${spec.DisplayName} entry ${idx + 1}`,
          );
        }
      });
    });

    if (
      formData.JobType === 'PredictiveHeatControl' ||
      formData.JobType === 'PredictiveCooling'
    ) {
      if (formData.UserInput.ControlType === 'pct') {
        if (!formData.UserInput.GatewayId || !formData.UserInput.Channel) {
          ok = false;
          alert('Missing required user input for PCT controller');
        }
      } else if (formData.UserInput.ControlType === 'ecl') {
        if (
          formData.UserInput.DeviceNumber == null ||
          formData.UserInput.CircuitNumber == null ||
          formData.UserInput.Offset == null
        ) {
          ok = false;
          alert('Missing required user input for ECL controller');
        }
      }
    }

    // HeatingSystemType required?
    const requireHst = (jobSpec?.UserInputSpecifications || []).some(
      (s) => s.Key === 'HeatingSystemType' && s.Required,
    );
    if (requireHst && !formData.UserInput.HeatingSystemType) {
      ok = false;
      alert('Missing required user input for Heating System Type');
    }

    return ok;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const finalFormData = { ...formData };

    // Prune controller fields if not relevant to this job type
    if (
      formData.JobType !== 'PredictiveHeatControl' &&
      formData.JobType !== 'PredictiveCooling'
    ) {
      delete finalFormData.UserInput.ControlType;
      delete finalFormData.UserInput.GatewayId;
      delete finalFormData.UserInput.Channel;
      delete finalFormData.UserInput.DeviceNumber;
      delete finalFormData.UserInput.CircuitNumber;
      delete finalFormData.UserInput.Offset;
    }

    submit(toPayload(finalFormData));
    onClose();
  };

  const jobSpec = jobSpecifications?.find(
    (j) => j.JobType === formData.JobType,
  );
  const controlType = formData.UserInput.ControlType || 'pct';

  const renderDataInputFields = (specifications) => {
    return (specifications?.DataInputSpecifications || []).map((spec) => {
      const isFieldEnabled = showOptionalFields[spec.Key] || spec.Required;

      return (
        <Box key={spec.Key} mb={2}>
          <Grid container alignItems="center">
            <Grid item sx={{ mb: 1 }}>
              {getIconForKey(spec.Key)}
            </Grid>
            <Grid item xs>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {spec.DisplayName}
              </Typography>
            </Grid>
            {!spec.Required && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!isFieldEnabled}
                      onChange={() => toggleOptionalField(spec.Key)}
                    />
                  }
                  label={`Enable`}
                />
              </Grid>
            )}
          </Grid>

          {isFieldEnabled && (
            <FormGroup>
              {[...Array(formData.DataInput[spec.Key]?.length || 1)].map(
                (_, index) => (
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 1 }}
                    key={`${spec.Key}-${index}`}
                    alignItems="center"
                  >
                    <Grid item xs={4}>
                      <Autocomplete
                        options={units || []}
                        getOptionLabel={(o) => o?.Name || ''}
                        filterOptions={(options, { inputValue }) => {
                          return (options || []).filter(
                            (o) =>
                              (o?.Name || '')
                                .toLowerCase()
                                .includes((inputValue || '').toLowerCase()) ||
                              String(o?.UnitId || '').includes(inputValue),
                          );
                        }}
                        value={getUnitById(
                          units,
                          formData.DataInput[spec.Key]?.[index]?.UnitId,
                        )}
                        isOptionEqualToValue={(o, v) => o?.UnitId === v?.UnitId}
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'UnitId',
                            value?.UnitId || '',
                          )
                        }
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Chip
                              icon={<LightRoundedIcon />}
                              label={option.UnitId}
                              size="small"
                              style={{ marginRight: 8, borderRadius: 1 }}
                            />
                            {option.Name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  {
                                    <Chip
                                      icon={<LightRoundedIcon />}
                                      label={
                                        formData.DataInput[spec.Key]?.[index]
                                          ?.UnitId
                                      }
                                      size="small"
                                      sx={{ mr: 1, borderRadius: 1 }}
                                    />
                                  }
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs>
                      <Autocomplete
                        options={
                          getUnitById(
                            units,
                            formData.DataInput[spec.Key]?.[index]?.UnitId,
                          )?.UnitSensors || []
                        }
                        getOptionLabel={(o) =>
                          o?.CustomName || o?.UnitTypeInput?.Name || ''
                        }
                        isOptionEqualToValue={(o, v) =>
                          o?.UnitTypeInput?.UnitTypeInputId ===
                          v?.UnitTypeInput?.UnitTypeInputId
                        }
                        value={getSensorByIds(
                          units,
                          formData.DataInput[spec.Key]?.[index]?.UnitId,
                          formData.DataInput[spec.Key]?.[index]
                            ?.UnitTypeInputId,
                        )}
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'UnitTypeInputId',
                            value?.UnitTypeInput?.UnitTypeInputId || '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit Sensor"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs>
                      <Autocomplete
                        options={['Minutes5', 'Hour']}
                        value={
                          formData.DataInput[spec.Key]?.[index]
                            ?.AggregateType || 'Minutes5'
                        }
                        isOptionEqualToValue={(o, v) => o === v}
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'AggregateType',
                            value,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Aggregate Type"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs>
                      <Autocomplete
                        options={(
                          getSensorByIds(
                            units,
                            formData.DataInput[spec.Key]?.[index]?.UnitId,
                            formData.DataInput[spec.Key]?.[index]
                              ?.UnitTypeInputId,
                          )?.UnitTypeInput?.SensorRequirements || []
                        ).map((r) => r.DataType)}
                        value={
                          formData.DataInput[spec.Key]?.[index]?.DataType || ''
                        }
                        isOptionEqualToValue={(o, v) => o === v}
                        onChange={(e, value) =>
                          handleDataInputChange(
                            spec.Key,
                            index,
                            'DataType',
                            value,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Data Type"
                            variant="filled"
                            size="small"
                            required={spec.Required}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={0.4} container justifyContent="flex-end">
                      {spec.AllowMultiple && (
                        <IconButton
                          sx={{ borderRadius: 1, ml: 1 }}
                          onClick={() =>
                            setFormData((prev) => {
                              const next = {
                                ...prev,
                                DataInput: { ...prev.DataInput },
                              };
                              if (index === 0) {
                                next.DataInput[spec.Key] = [
                                  ...(next.DataInput[spec.Key] || []),
                                  {},
                                ];
                              } else {
                                next.DataInput[spec.Key] = (
                                  next.DataInput[spec.Key] || []
                                ).filter((_, i) => i !== index);
                              }
                              return next;
                            })
                          }
                        >
                          {index === 0 ? <AddIcon /> : <RemoveIcon />}
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ),
              )}
            </FormGroup>
          )}
        </Box>
      );
    });
  };

  const renderUserInputFields = () => {
    if (
      formData.JobType !== 'PredictiveHeatControl' &&
      formData.JobType !== 'PredictiveCooling'
    )
      return null;

    return (
      <Box>
        <Box display="flex">
          <RouterRoundedIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body1">Controller Settings</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth margin="normal">
              <RadioGroup
                value={controlType}
                onChange={(e) =>
                  handleUserInputChange('ControlType', e.target.value)
                }
                row
              >
                <FormControlLabel
                  value="pct"
                  control={<Radio size="small" />}
                  label="PCT"
                />
                <FormControlLabel
                  value="ecl"
                  control={<Radio size="small" />}
                  label="ECL"
                />
                <FormControlLabel
                  value="bravida"
                  control={<Radio size="small" />}
                  label="Bravida"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        {controlType === 'pct' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <Autocomplete
                options={gateways || []}
                getOptionLabel={(o) => o?.Name || ''}
                isOptionEqualToValue={(o, v) => o?.GatewayId === v?.GatewayId}
                value={
                  (gateways || []).find(
                    (g) => g.GatewayId === formData.UserInput.GatewayId,
                  ) || null
                }
                onChange={(e, value) =>
                  handleUserInputChange('GatewayId', value?.GatewayId || '')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gateway"
                    variant="filled"
                    size="small"
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={CHANNEL_OPTIONS}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o?.value === v?.value}
                value={
                  CHANNEL_OPTIONS.find(
                    (x) => x.value === formData.UserInput.Channel,
                  ) || null
                }
                onChange={(e, value) =>
                  handleUserInputChange('Channel', value?.value || '')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Actuator Channel"
                    variant="filled"
                    size="small"
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        {controlType === 'ecl' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <TextField
                label="Device Number"
                type="number"
                variant="filled"
                fullWidth
                size="small"
                value={formData.UserInput.DeviceNumber ?? ''}
                onChange={(e) =>
                  handleUserInputChange(
                    'DeviceNumber',
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Circuit Number"
                type="number"
                variant="filled"
                fullWidth
                size="small"
                value={formData.UserInput.CircuitNumber ?? ''}
                onChange={(e) =>
                  handleUserInputChange(
                    'CircuitNumber',
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Timezone Offset"
                variant="filled"
                fullWidth
                size="small"
                value={formData.UserInput.Offset ?? ''}
                onChange={(e) =>
                  handleUserInputChange(
                    'Offset',
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
              />
            </Grid>
          </Grid>
        )}

        {controlType === 'bravida' && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <TextField
                label="Building Name"
                variant="filled"
                fullWidth
                size="small"
                value={formData.UserInput.BuildingName || ''}
                onChange={(e) =>
                  handleUserInputChange('BuildingName', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Heating Circuit Name"
                variant="filled"
                fullWidth
                size="small"
                value={formData.UserInput.HeatingCircuitName || ''}
                onChange={(e) =>
                  handleUserInputChange('HeatingCircuitName', e.target.value)
                }
              />
            </Grid>
          </Grid>
        )}

        {jobSpec?.UserInputSpecifications?.some(
          (s) => s.Key === 'HeatingSystemType',
        ) && (
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Heating System Type</InputLabel>
                <Select
                  variant="filled"
                  value={formData.UserInput.HeatingSystemType || ''}
                  onChange={(e) =>
                    handleUserInputChange('HeatingSystemType', e.target.value)
                  }
                >
                  {jobSpec.UserInputSpecifications.find(
                    (s) => s.Key === 'HeatingSystemType',
                  )?.InputParameters?.ValidValues?.map((opt) => (
                    <MenuItem key={opt.Value} value={opt.Value}>
                      {opt.Text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {jobSpec?.UserInputSpecifications?.some(
          (s) => s.Key === 'RiskOfFrostDamage',
        ) && (
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" size="small">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.UserInput.RiskOfFrostDamage}
                      onChange={(e) =>
                        handleUserInputChange(
                          'RiskOfFrostDamage',
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label="Risk of Frost Damage"
                />
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Edit Job Subscription</DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth margin="normal" size="small">
          <Autocomplete
            options={jobSpecifications || []}
            getOptionLabel={(o) => o?.DisplayName || ''}
            value={
              (jobSpecifications || []).find(
                (j) => j.JobType === formData.JobType,
              ) || null
            }
            isOptionEqualToValue={(o, v) => o?.JobType === v?.JobType}
            onChange={() => {
              /* JobType typically locked in edit mode; change not allowed */
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Type"
                variant="filled"
                size="small"
              />
            )}
            disabled
          />
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Display Name"
          variant="filled"
          size="small"
          value={formData.DisplayName}
          onChange={(e) =>
            setFormData({ ...formData, DisplayName: e.target.value })
          }
        />

        {!!formData.JobType && (
          <Grid container spacing={4}>
            <Grid item xs={12} lg={12}>
              {renderDataInputFields(jobSpec)}
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {(formData.JobType === 'PredictiveCooling' ||
              formData.JobType === 'PredictiveHeatControl') && (
              <Grid item xs={12} lg={12}>
                {renderUserInputFields()}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <Button variant="text" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} size="small">
          {isLoading ? <CircularProgress color="inherit" size={40} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobSubscriptionEdit;
